import React, { useEffect, useRef } from "react";
import { RadioGroup, Select, TextArea, TextInput } from "components/flexible_testing/shared/form";
import { useField, useFormikContext } from "formik";
import TestCaseFields from "./test_case_fields";
import ScopeFields from "./scope_fields";
import { Activity } from "../activity_step_content";
import { RequestedTest } from "../requested_test_form";
import { RequestedTest as GraphQLRequestedTest } from "types/graphql";
import { useEnvironments } from "../targeting_step_content";
import RequestedTestWizardNavigation from "../requested_test_wizard_navigation";
import { useFeatureFlag } from "app/feature_flags";
import { useRequestedTests } from "./use_requested_tests";
import { useCurrentUser } from "app/current_user/use_current_user";
import { StatusCard } from "@hopper/status_card";

export const ACTIVITIES_REQUIRING_SCOPE = [Activity.Exploratory, Activity.Translation, Activity.Localization];
export const ACTIVITIES_REQUIRING_TEST_CASE_FIELDS = [Activity.TestCaseRun];

const DEFAULT_LABELS = {
  requiresFinancialTransactions: "Do testers need to conduct financial transactions?",
  requiresKycIdentityCheck: "Do testers need to go through a KYC/identity check?",
  requiresPersonalIdentifyingInformation: "Do testers need to provide personal identifying information?",
  requiresMovingToDifferentLocation: "Do testers need to move to a different location during the test?",
} as const;

const LABELS_BY_ACTIVITY = {
  [Activity.Exploratory]: DEFAULT_LABELS,
  [Activity.Localization]: DEFAULT_LABELS,
  [Activity.Translation]: DEFAULT_LABELS,
  [Activity.TestCaseRun]: {
    requiresFinancialTransactions: "Do your test cases require financial transactions?",
    requiresKycIdentityCheck: "Do your test cases involve a KYC/identity check?",
    requiresPersonalIdentifyingInformation: "Do your test cases require personal identifying information?",
    requiresMovingToDifferentLocation: "Do your test cases require moving to a different location?",
  },
} as const;

const ScopeStepContent = ({ nextStep }: { nextStep: () => unknown }) => {
  const { isValid, values, validateForm } = useFormikContext<RequestedTest>();
  const activity = values.activity;
  const [_input_props, _meta_props, { setValue: setKilStartingFromTestCycleCode }] =
    useField("kilStartingFromTestCycleCode");
  const initialRender = useRef<boolean>(true);
  const routingUniqueTestDataEnabled = useFeatureFlag("routing-unique-test-data", false);
  const { requestedTests, loading } = useRequestedTests({ platform: values.applicationType });
  const currentUser = useCurrentUser();
  const autoLaunchingEnabled = currentUser?.organization?.autoLaunchingEnabled;
  const requestedTestsForSelect = [
    { label: "", value: "" },
    ...requestedTests
      .map((requestedTest: GraphQLRequestedTest) => ({
        label: requestedTest.name,
        value: requestedTest.testCycleCode?.toString() || "",
      }))
      .reverse(),
  ];
  useEffect(() => {
    initialRender.current = false;
    validateForm();

    if (autoLaunchingEnabled && !loading && requestedTestsForSelect.length > 1 && requestedTestsForSelect[1]) {
      setKilStartingFromTestCycleCode(requestedTestsForSelect[1].value);
    }
  }, [loading]);

  useEnvironments({ platform: values.applicationType, skip: initialRender.current || !isValid });

  return (
    <div data-testid="scope-step-content">
      <div className="grid grid-cols-2 gap-4xl">
        <div className="flex flex-col gap-lg" style={{ maxWidth: "430px" }}>
          {ACTIVITIES_REQUIRING_TEST_CASE_FIELDS.includes(activity) && <TestCaseFields />}
          {ACTIVITIES_REQUIRING_SCOPE.includes(activity) && <ScopeFields />}
          {routingUniqueTestDataEnabled && (
            <div className="p-md text-sm bg-light border-l-2 border-primary w-full">
              If testers need to use pre-defined user accounts created by you, you can provide them as <b>Test data</b>
            </div>
          )}
          {!routingUniqueTestDataEnabled && (
            <div>
              <RadioGroup
                name="requiresPredefinedUserAccounts"
                label="Do testers need to test with existing user accounts that you have created?"
                options={[
                  { id: "requiresPredefinedUserAccountsYes", label: "Yes", value: "true" },
                  { id: "requiresPredefinedUserAccountsNo", label: "No", value: "false" },
                ]}
                required
              />
              {values.requiresPredefinedUserAccounts === "true" && (
                <div>
                  <TextArea
                    name="predefinedUserAccountsInstructions"
                    placeholder="e.g., Admin - username & password"
                    label="What are the account types and credentials?"
                    description="This information will be shown directly to testers"
                    useHopperInput={true}
                    required
                  />
                </div>
              )}
            </div>
          )}
          {!autoLaunchingEnabled && (
            <>
              <RadioGroup
                name="hasKnownIssuesList"
                label="Do you have a known issues list?"
                options={[
                  { id: "hasKnownIssuesListYes", label: "Yes", value: "true" },
                  { id: "hasKnownIssuesListNo", label: "No", value: "false" },
                ]}
                required
              />
              {values.hasKnownIssuesList === "true" && (
                <div>
                  <TextInput
                    name="knownIssuesListUrl"
                    placeholder="https://known-issues-list.com"
                    label="What is the URL where we can find the known issues list?"
                    required
                  />
                </div>
              )}
            </>
          )}
          {autoLaunchingEnabled && !loading && (
            <>
              <div className="bg-light text-sm">
                <StatusCard statusSize="sm">
                  Known issues provide valuable context about the current state of the product.
                  <br />
                  Using a Known issues list for this test can ultimately lead to more efficient testing and actionable
                  insights, by reducing duplicate bug reports, saving time for testers, and ensuring that testing
                  focuses on uncovering new problems rather than revisiting already identified ones.
                </StatusCard>
              </div>
              <Select
                label={
                  <span className="text-body leading-150">
                    Include known issues starting from test (only 10 latest tests can be selected)
                  </span>
                }
                name="kilStartingFromTestCycleCode"
                options={requestedTestsForSelect}
                selectedValue={requestedTestsForSelect[1]?.value}
                isOptional={false}
              />
            </>
          )}
        </div>
        <div className="flex flex-col gap-lg">
          <RadioGroup
            name="requiresFinancialTransactions"
            label={LABELS_BY_ACTIVITY[activity]["requiresFinancialTransactions"]}
            options={[
              { id: "requiresFinancialTransactionsYes", label: "Yes", value: "true" },
              { id: "requiresFinancialTransactionsNo", label: "No", value: "false" },
            ]}
            required
          />
          {values.requiresFinancialTransactions === "true" && (
            <div>
              <TextArea
                name="financialTransactionsInstructions"
                placeholder="e.g., Google Pay"
                label="What payment method(s) do the testers need to use?"
                useHopperInput={true}
                required
              />
            </div>
          )}
          <RadioGroup
            name="requiresKycIdentityCheck"
            label={LABELS_BY_ACTIVITY[activity]["requiresKycIdentityCheck"]}
            options={[
              { id: "requiresKycIdentityCheckYes", label: "Yes", value: "true" },
              { id: "requiresKycIdentityCheckNo", label: "No", value: "false" },
            ]}
            required
          />
          {values.requiresKycIdentityCheck === "true" && (
            <div>
              <TextArea
                name="kycIdentityCheckInstructions"
                placeholder="e.g., A valid passport"
                label="What KYC/Identity documents and data are needed?"
                useHopperInput={true}
                required
              />
            </div>
          )}
          <RadioGroup
            name="requiresPersonalIdentifyingInformation"
            label={LABELS_BY_ACTIVITY[activity]["requiresPersonalIdentifyingInformation"]}
            options={[
              { id: "requiresPersonalIdentifyingInformationYes", label: "Yes", value: "true" },
              { id: "requiresPersonalIdentifyingInformationNo", label: "No", value: "false" },
            ]}
            required
          />
          {values.requiresPersonalIdentifyingInformation === "true" && (
            <div>
              <TextArea
                name="personalIdentifyingInformationInstructions"
                placeholder="e.g., Passport number"
                label="What information do the testers need to provide?"
                useHopperInput={true}
                required
              />
            </div>
          )}
          <RadioGroup
            name="requiresMovingToDifferentLocation"
            label={LABELS_BY_ACTIVITY[activity]["requiresMovingToDifferentLocation"]}
            options={[
              { id: "requiresMovingToDifferentLocationYes", label: "Yes", value: "true" },
              { id: "requiresMovingToDifferentLocationNo", label: "No", value: "false" },
            ]}
            required
          />
          {values.requiresMovingToDifferentLocation === "true" && (
            <div>
              <TextArea
                name="movingToDifferentLocationInstructions"
                placeholder="e.g., Travel to a nearby retail location"
                label="What do the testers need to do?"
                useHopperInput={true}
                required
              />
            </div>
          )}
        </div>
      </div>
      <RequestedTestWizardNavigation onSubmit={nextStep} submitDisabled={!isValid} />
    </div>
  );
};

export default ScopeStepContent;
